import React from 'react';
import BlogLandingPage, { BlogConfig } from '../../components/BlogLandingPage';
import iphoneBatteryAd from '../../assets/blogs/iphoneBatteryAd.jpeg';

const config: BlogConfig = {
  slug: 'iphone-14-battery-replacement',
  heroImage: iphoneBatteryAd,
  title: 'iPhone 14 Battery Replacement — 180-Day Warranty, Doorstep Service',
  subtitle: 'iPhone 14 battery draining too fast or not lasting the day? Our certified technicians come to you anywhere in Mumbai and replace your battery in under an hour.',
  heroLabel: 'iPhone 14 Battery Repair',
  intro:
    'The iPhone 14 comes with a 3,279 mAh battery — a modest bump over its predecessor, but still subject to the same lithium-ion degradation over time. Most users start noticing significant battery drain within 12–24 months: faster discharge, slower charge, and in some cases unexpected shutdowns even when the indicator shows 20–25% remaining. A battery replacement at this stage is the most cost-effective fix — far cheaper than upgrading your iPhone 14, and it makes the device feel brand new. minto sends a certified technician to your home or office in Mumbai, replaces the battery on-site in 45 minutes, and backs it with a 180-day warranty.',
  warrantyNote:
    'Every iPhone 14 battery replaced by minto carries a 180-day warranty. If you experience battery drain, sudden shutdowns, swelling, overheating during charge, or charging failure within 180 days of the replacement — we fix it at no extra cost. Water damage and physical breakage are not covered under warranty.',
  signs: [
    'iPhone 14 battery drains faster than it did when the phone was new',
    'Phone shuts off suddenly while there is still charge shown',
    'Back of the iPhone 14 feels warm even during idle use',
    'Charging slows down significantly or stops mid-charge',
    'Battery percentage drops quickly from 30% to 0%',
    'Screen dims and performance throttles during heavy tasks',
    'Battery health reads below 80% in Settings → Battery',
  ],
  sections: [
    {
      heading: 'Why iPhone 14 Batteries Degrade Over Time',
      body: 'Like all lithium-ion batteries, the iPhone 14\'s battery chemically degrades with each charge cycle. Factors like frequent fast charging, high ambient temperatures, leaving the iPhone 14 plugged in overnight at 100%, and deep discharge below 5% all accelerate wear. The device\'s performance management system kicks in once health drops below 80% — reducing peak CPU and GPU output to prevent unexpected shutdowns. This is why the iPhone 14 feels less snappy even though no hardware has changed.',
      bullets: [
        'Charging to 100% repeatedly accelerates capacity loss',
        'Sustained heat (cars in summer, direct sunlight) permanently damages cells',
        'Using power-intensive apps with low battery stresses degraded cells',
        'Incompatible third-party chargers can overcharge and damage the battery',
      ],
    },
    {
      heading: 'iPhone 14 vs iPhone 13 Battery Replacement — Key Differences',
      body: 'The iPhone 14 uses a slightly larger battery cell and a revised rear chassis design compared to the iPhone 13. Our technicians are specifically trained on the iPhone 14 form factor — the adhesive strips, rear glass bonding method, and cable routing differ from earlier models. Using a technician unfamiliar with the iPhone 14 series risks damaging the rear panel or the charging coil during disassembly.',
      bullets: [
        'iPhone 14 uses stronger rear glass adhesive — requires precision heat application',
        'Battery connector location differs from iPhone 13 — different disassembly sequence',
        'Wireless charging coil runs along the battery edge — care needed during removal',
        'All our technicians are trained model-specifically before servicing',
      ],
    },
    {
      heading: 'What to Expect During Your iPhone 14 Doorstep Appointment',
      body: 'Booking a doorstep iPhone 14 battery replacement with minto is straightforward. Select your device, choose a time slot, and a technician arrives with the replacement battery and all tools. You do not need to back up your data or remove your SIM — battery replacement does not affect storage or software. The technician will run a pre- and post-repair diagnostic and show you the battery health reading after installation.',
      bullets: [
        'Technician arrives within the booked time window',
        'Pre-repair battery health and voltage check performed',
        'Battery replaced entirely in your presence',
        'Post-repair capacity and charging test completed on-site',
        '180-day warranty slip issued before the technician leaves',
      ],
    },
  ],
  faqs: [
    {
      q: 'How long does iPhone 14 battery replacement take at home?',
      a: 'The replacement takes approximately 40–50 minutes at your location. The iPhone 14 rear glass requires careful heat-based removal, so we allow slightly more time than older models.',
    },
    {
      q: 'What is the warranty on the iPhone 14 replacement battery?',
      a: 'You get a 180-day warranty. Any battery capacity issues, unexpected shutdowns, or charging failures within 180 days are covered free of charge. Physical damage and water damage are excluded.',
    },
    {
      q: 'Is my iPhone 14 data safe during battery replacement?',
      a: 'Yes, completely. We access only the rear panel and battery compartment. No data, SIM, or software is touched during the repair.',
    },
    {
      q: 'How do I check if my iPhone 14 battery actually needs replacement?',
      a: 'Open Settings → Battery → Battery Health & Charging. If Maximum Capacity shows below 80%, or if you see a "Service Recommended" message, it is time to replace. You can also WhatsApp us for a free remote diagnosis.',
    },
    {
      q: 'Do you service the iPhone 14 Plus and Pro models too?',
      a: 'Yes, we service all iPhone 14 variants including the standard, Plus, Pro, and Pro Max. Each variant has a different battery capacity and we carry model-specific replacement parts.',
    },
    {
      q: 'What areas of Mumbai do you cover for iPhone 14 battery repair?',
      a: 'We cover all of Mumbai including Andheri, Bandra, Borivali, Malad, Goregaon, Dadar, Lower Parel, Ghatkopar, Kurla, Thane, Chembur, and Navi Mumbai — 7 days a week.',
    },
  ],
  seo: {
    title: 'iPhone 14 Battery Replacement Mumbai | 180-Day Warranty | minto Repairs',
    description: 'iPhone 14 battery draining too fast? Get doorstep battery replacement in Mumbai. Certified technicians, OEM-grade battery, 180-day warranty. Book online in 2 minutes. Independent service — not affiliated with Apple Inc.',
    keywords: 'iphone 14 battery replacement, iphone 14 battery repair mumbai, doorstep battery replacement iphone 14, iphone 14 battery draining fast, iphone 14 pro battery replacement, iphone 14 plus battery repair, iphone 14 battery health, mobile battery repair mumbai, minto repairs',
  },
};

const iPhone14BatteryPage: React.FC = () => <BlogLandingPage blog={config} />;
export default iPhone14BatteryPage;
